import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { getSharedState } from '../utils/sharedStateUtils';
import { computeAvailableFilters } from '../utils/filters';
import { RemoteComponentStore, SharedState } from 'interfaces/filterContext';
import { RemoteComponentStoreContextProps } from 'interfaces/remoteContext';

const RemoteComponentStoreContext = createContext<RemoteComponentStoreContextProps | undefined>(undefined);

export const RemoteComponentStoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [remoteComponentStore, setRemoteComponentStore] = useState<RemoteComponentStore>({});
    const [sharedState, setSharedState] = useState<SharedState>({
        selectedFilters: {},
        filterKeyMapping: {},
        availableFilters: {},
    });

    // Internal store for non-reactive values
    const internalStoreRef = useRef<Record<string, any>>({});
    const stateRef = useRef(sharedState);
    const pendingUpdateRef = useRef<NodeJS.Timeout | null>(null);

    // Subscription management
    const subscribersRef = useRef<Record<string, Set<(value: any) => void>>>({});

    // Keep the ref in sync with the state
    stateRef.current = sharedState;

    // Compute available filters using useMemo
    const computedAvailableFilters = useMemo(() => {
        const selectedFilters = sharedState.selectedFilters || {};
        const filterKeyMapping = sharedState.filterKeyMapping || {};
        const computedFilters = computeAvailableFilters(selectedFilters, filterKeyMapping);
        return computedFilters;
    }, [sharedState.selectedFilters, sharedState.filterKeyMapping]);

    // Update sharedState only when computedAvailableFilters changes
    useEffect(() => {
        setSharedState((prevState) => {
            if (prevState.availableFilters !== computedAvailableFilters) {
                return {
                    ...prevState,
                    availableFilters: computedAvailableFilters,
                };
            }
            return prevState;
        });
    }, [computedAvailableFilters]);

    const getSharedStateCallback = useCallback(
        (key: string) => {
            // Special case: empty string returns the entire internal store
            if (key === '') {
                return internalStoreRef.current;
            }

            // First check the internal store for non-reactive values
            if (internalStoreRef.current.hasOwnProperty(key)) {
                return internalStoreRef.current[key];
            }

            // Then check the reactive state
            return getSharedState(stateRef.current, key);
        },
        []
    );

    const setSharedStateValueCallback = useCallback(
        (key: string, value: any) => {
            // Check if the value is actually different
            if (internalStoreRef.current[key] === value) {
                return;
            }

            // Update only the internal store (no React re-render)
            internalStoreRef.current = {
                ...internalStoreRef.current,
                [key]: value
            };

            // Notify subscribers for this specific key
            const keySubscribers = subscribersRef.current[key];
            if (keySubscribers) {
                keySubscribers.forEach(callback => callback(value));
            }

            // Notify subscribers for pattern-based keys (e.g., refreshInfo updates)
            if (key.includes(':refreshInfo')) {
                const patternSubscribers = subscribersRef.current['refreshInfo'];
                if (patternSubscribers) {
                    patternSubscribers.forEach(callback => callback(value));
                }
            }
        },
        []
    );

    const subscribeToUpdates = useCallback(
        (key: string, callback: (value: any) => void) => {
            if (!subscribersRef.current[key]) {
                subscribersRef.current[key] = new Set();
            }
            subscribersRef.current[key].add(callback);

            // Return unsubscribe function
            return () => {
                subscribersRef.current[key]?.delete(callback);
                if (subscribersRef.current[key]?.size === 0) {
                    delete subscribersRef.current[key];
                }
            };
        },
        []
    );

    const setSharedStateCallback = useCallback(
        (updater: React.SetStateAction<SharedState>) => {
            let newState: SharedState;
            if (typeof updater === 'function') {
                newState = updater(stateRef.current);
            } else {
                newState = updater;
            }

            // Check if the state is actually different
            if (JSON.stringify(stateRef.current) === JSON.stringify(newState)) {
                return;
            }

            // Update the ref immediately
            stateRef.current = newState;

            // Clear any pending update
            if (pendingUpdateRef.current) {
                clearTimeout(pendingUpdateRef.current);
            }

            // Schedule a single state update (this will trigger React re-renders)
            pendingUpdateRef.current = setTimeout(() => {
                setSharedState(newState);
                pendingUpdateRef.current = null;
            }, 0);
        },
        []
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (pendingUpdateRef.current) {
                clearTimeout(pendingUpdateRef.current);
            }
        };
    }, []);

    return (
        <RemoteComponentStoreContext.Provider
            value={{
                remoteComponentStore,
                setRemoteComponentStore,
                sharedState,
                setSharedState: setSharedStateCallback,
                getSharedState: getSharedStateCallback,
                setSharedStateValue: setSharedStateValueCallback,
                subscribe: subscribeToUpdates,
            }}
        >
            {children}
        </RemoteComponentStoreContext.Provider>
    );
};

export const useRemoteComponentStore = (): RemoteComponentStoreContextProps => {
    const context = useContext(RemoteComponentStoreContext);
    if (!context) {
        throw new Error('useRemoteComponentStore must be used within a RemoteComponentStoreProvider');
    }
    return context;
};